import { useState } from "react"

import { App, Tooltip } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../../Components/Button"
import { RoutesPaths } from "../../../../constants/user-routes"
import Icon from "../../../../Icon"
import { highAccessRoles } from "../../../../Utils/permission"
import { RECEIPT_CONFIGURE_SLUGS } from "../../constants/receipt-configure-slugs"
import { SECTION_KEYS, SECTION_LABELS } from "../../constants/steps"
import { saveDraft, submitProfile, submitProfileUpdate } from "../constants/externalOps"
import {
  setPendingChanges as setPendingChangesAction,
  setWizard as setWizardAction,
  stopSuggestions as stopSuggestionsAction
} from "../redux/configure.slice"
import { wizardView } from "../utils/hydrate"
import { allSectionCounts } from "../utils/sectionProgress"
import { areRequiredFilled, isNextEnabled } from "../utils/sectionValidation"

import styles from "../shell.module.less"

const UNDER_REVIEW_MESSAGE =
  "Your profile changes are under review. You can edit again once they're processed."

const ConfigureFooter = ({
  currentSection,
  buildingBlocks,
  dirtySections,
  wizard,
  isHydrated,
  baseVersionId,
  baRoleId,
  isMobile,
  setPendingChanges,
  setWizard,
  stopSuggestions,
  onRequestNext
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { modal, notification } = App.useApp()
  const [busy, setBusy] = useState(false)

  // Leaving Step 3 stops a first fill still on its way before the save reads the fields:
  // landing after that, it would show values the save never sent (CHA1-4909).
  const saveCurrentSection = (committedSteps) => {
    if (currentSection === RECEIPT_CONFIGURE_SLUGS.payment_identifiers) stopSuggestions()
    return saveDraft(buildingBlocks, { committedSteps, fromSection: currentSection })
  }

  // Backend rejections carry one line per field, so they go in the wrapping
  // description. Separators are off because the text is a sentence, not an i18n
  // key: its punctuation would otherwise be read as a nested lookup.
  const showError = (errors) =>
    notification.error({
      message: t("Could not save"),
      description: (
        <span style={{ whiteSpace: "pre-line" }}>
          {t(errors || "Something went wrong. Please try again.", {
            keySeparator: false,
            nsSeparator: false,
            defaultValue: errors
          })}
        </span>
      )
    })

  const idx = SECTION_KEYS.indexOf(currentSection)
  const prevKey = idx > 0 ? SECTION_KEYS[idx - 1] : null
  const nextKey = idx >= 0 && idx < SECTION_KEYS.length - 1 ? SECTION_KEYS[idx + 1] : null

  const sectionState = buildingBlocks?.[currentSection] ?? {}
  const nextEnabled = isNextEnabled(currentSection, sectionState, buildingBlocks)
  // Next-button gate: only "required filled". Format validity is checked on
  // Next click; this way Next greys out until required fields exist but never
  // turns red before the click.
  const requiredFilled = areRequiredFilled(currentSection, sectionState, buildingBlocks)

  // The final Submit performs the enrollment, so it must stay locked until
  // EVERY required step is complete — not just the current one (a merchant can
  // jump between steps via the rail). On the last step we gate on the whole
  // wizard.
  const isLastStep = !nextKey
  const allRequiredComplete = SECTION_KEYS.every((k) =>
    isNextEnabled(k, buildingBlocks?.[k] || {}, buildingBlocks)
  )
  const submitLocked = isLastStep && !allRequiredComplete

  const view = wizardView(wizard)
  // §11.15: only Administrator & Owner may edit; everyone else is view-only.
  const canEdit = (!baRoleId || highAccessRoles.includes(baRoleId)) && view.isEditable
  // §11.14: any open Profile-update record locks the whole form.
  const underReview = view.isUnderReview

  // §11.11's three submit states: first time -> Submit; already submitted with
  // no changes -> no button; already submitted with changes -> Update details.
  // "Already submitted" is the server's is_first_submission, never a local
  // marker.
  const alreadySubmitted = view.profileFinished
  const hasDirtyChanges = Object.values(dirtySections || {}).some(Boolean)
  const finishLabel = alreadySubmitted ? t("Update details") : t("Submit")
  // Only meaningful on the last step — "Next" on earlier steps is never hidden.
  const hideFinishButton =
    isLastStep && (!canEdit || underReview || (alreadySubmitted && !hasDirtyChanges))

  // First time -> initial submit (creates the live version + Pending records).
  // Already submitted -> a Profile update against the base version the wizard
  // was loaded with (Step 3 is read-only by then, so only content changes).
  const finish = async () => {
    if (alreadySubmitted) {
      const result = await submitProfileUpdate({
        buildingBlocks,
        baseVersionId
      })
      if (!result.ok) {
        showError(result.errors)
        return
      }
      setPendingChanges(true)
      notification.success({
        message: t(
          "Your Digital Receipt changes are under review. We'll notify you when they're applied."
        )
      })
    } else {
      const result = await submitProfile(buildingBlocks)
      if (!result.ok) {
        showError(result.errors)
        return
      }
      // No local "finished" marker is written: the next Configure read reports
      // is_first_submission=false, and until it confirms that, nothing may
      // claim the setup succeeded.
      notification.success({ message: t("Enrollment started") })
    }
    navigate(RoutesPaths.receipts.all)
  }

  // The final submit/update sends the whole profile in one call, so there is
  // nothing to commit beforehand.
  const commitAndFinish = async () => {
    setBusy(true)
    await finish()
    setBusy(false)
  }

  // Last step. If any section still has unanswered questions, confirm before
  // finishing (and reassure them progress is saved + they can come back). If
  // everything is already complete, finish straight away — no nagging.
  const handleFinish = () => {
    const counts = allSectionCounts(buildingBlocks)
    const incomplete = SECTION_KEYS.filter((k) => {
      // The Preview step has no form questions, so it can never be "answered".
      // Exclude it from the completeness confirm where it would only ever be a
      // no-op.
      if (k === RECEIPT_CONFIGURE_SLUGS.preview) return false
      const c = counts[k] || { answered: 0, total: 0 }
      return !(c.total > 0 && c.answered === c.total)
    })
    if (incomplete.length === 0) {
      commitAndFinish()
      return
    }
    modal.confirm({
      title: t("Finish setup now?"),
      content: t(
        "Some steps still have unanswered questions. You can finish now and complete them whenever you like; your progress is always saved."
      ),
      okText: t("Finish now"),
      cancelText: t("Keep editing"),
      centered: !isMobile,
      onOk: commitAndFinish
    })
  }

  const handleSaveForLater = async () => {
    if (!isHydrated) return
    setBusy(true)
    // §11.5: Save for later commits nothing new — only the steps Next already committed.
    const result = await saveCurrentSection(wizard?.completedSteps || [])
    setBusy(false)
    if (!result.ok) {
      showError(result.errors)
      return
    }
    setWizard({ wizard: result.state.wizard })
    notification.success({
      message: t("Saved"),
      description: t("We'll keep your progress. You can come back any time.")
    })
    navigate(RoutesPaths.receipts.all)
  }

  const handleNext = async () => {
    if (!isHydrated) return
    if (!nextEnabled) {
      onRequestNext?.(false)
      notification.warning({
        message: t("Some fields need attention"),
        description: t("Please fix the highlighted fields before moving on.")
      })
      return
    }
    // Final step: route through the finish confirmation instead of advancing.
    if (!nextKey) {
      handleFinish()
      return
    }
    setBusy(true)
    // Next is the only action that commits a step (§11.5), so the current one joins the set.
    const result = await saveCurrentSection([...(wizard?.completedSteps || []), currentSection])
    setBusy(false)
    if (!result.ok) {
      showError(result.errors)
      return
    }
    // The save reports which steps the server now counts as complete, so the
    // rail marks this one Done straight away instead of waiting for the next
    // page load.
    setWizard({ wizard: result.state.wizard })
    navigate(RoutesPaths.receipts.configure_profile_hash(nextKey))
  }

  return (
    <div className={styles.footer}>
      <div className={styles.footerLeft}>
        {/* §11.15: view-only roles never see Save for later at all. §11.14:
            under review, it's visible but disabled with a tooltip. */}
        {canEdit ? (
          <Tooltip title={underReview ? t(UNDER_REVIEW_MESSAGE) : ""}>
            <span>
              <Button
                type="default"
                loading={busy}
                disabled={underReview}
                onClick={handleSaveForLater}
                text={t("Save for later")}
                data-testid="deflect-configure-save-later-btn"
              />
            </span>
          </Tooltip>
        ) : null}
      </div>
      <div className={styles.footerCenter}>
        {busy ? (
          <span className={cls(styles.saveStatus, styles.saving)}>{`${t("Saving")}…`}</span>
        ) : null}
      </div>
      <div className={styles.footerRight}>
        {prevKey ? (
          <Button
            type="default"
            icon={<Icon type="arrow_left" className={styles.navArrow} />}
            onClick={() => {
              // Never before hydration: that would push whatever the store
              // still held back to the server as if the merchant had typed it.
              if (!isHydrated) {
                navigate(RoutesPaths.receipts.configure_profile_hash(prevKey))
                return
              }
              // Same guard as LeftRail.handleStepClick (CHA1-2123): don't let
              // the merchant walk backwards while the current step was already
              // committed but is now invalid — saveDraft would otherwise
              // rewrite `completed_steps` on the server and move the frontier
              // back.
              const currentWasCommitted = wizard?.completedSteps?.includes(currentSection)
              if (currentWasCommitted && !nextEnabled) {
                onRequestNext?.(false)
                notification.warning({
                  message: t("Some fields need attention"),
                  description: t("Please fix the highlighted fields before moving on.")
                })
                return
              }
              // Previous is navigation, not Next: it commits nothing new (§11.5).
              saveCurrentSection(wizard?.completedSteps || [])
              navigate(RoutesPaths.receipts.configure_profile_hash(prevKey))
            }}
            text={`${t("Previous")}: ${t(SECTION_LABELS[prevKey])}`}
            data-testid="deflect-configure-prev-btn"
          />
        ) : null}
        {hideFinishButton ? null : (
          <Tooltip
            title={
              submitLocked
                ? t("Complete all required steps before you enroll")
                : !requiredFilled
                  ? t("Fill in all required fields to continue")
                  : ""
            }
          >
            <span>
              <Button
                type="primary"
                // Next is greyed until required fields are filled (CHA1-2009).
                // On the final step Submit is additionally locked until every
                // section is complete so enrollment can't start early.
                disabled={busy || submitLocked || !requiredFilled}
                loading={busy}
                className={styles.navButton}
                icon={nextKey ? <Icon type="arrow_right" className={styles.navArrow} /> : null}
                iconPosition="end"
                onClick={handleNext}
                text={nextKey ? `${t("Next")}: ${t(SECTION_LABELS[nextKey])}` : finishLabel}
                data-testid="deflect-configure-next-btn"
              />
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  buildingBlocks: state.receiptConfigure.profile.buildingBlocks,
  dirtySections: state.receiptConfigure.ui?.dirtySections,
  wizard: state.receiptConfigure.profile.wizard,
  isHydrated: !!state.receiptConfigure.profile.hydratedBaId,
  baseVersionId: state.receiptConfigure.profile.baseVersionId,
  baRoleId: state.auth?.userInfo?.ba_role?.id
})
const mapDispatchToProps = (dispatch) => ({
  setPendingChanges: (v) => dispatch(setPendingChangesAction(v)),
  setWizard: (payload) => dispatch(setWizardAction(payload)),
  stopSuggestions: () => dispatch(stopSuggestionsAction())
})

export default connect(mapStateToProps, mapDispatchToProps)(ConfigureFooter)
